import { memo, useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';

interface Props {
  isOpen: boolean;
  title: string;
  message: string;
  /** Optional rich content rendered between the message and the buttons (e.g. a list of what gets deleted). */
  details?: ReactNode;
  /** When set, the confirm button stays disabled until the user types this exact text. */
  requireText?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmationModal: FC<Props> = memo(function ConfirmationModal({
  isOpen,
  title,
  message,
  details,
  requireText,
  confirmText = 'Leave',
  cancelText = 'Stay',
  onConfirm,
  onCancel,
}) {
  const [typedText, setTypedText] = useState('');
  useEffect(() => { if (!isOpen) setTypedText(''); }, [isOpen]);

  if (!isOpen) return null;

  const confirmBlocked = !!requireText && typedText.trim() !== requireText.trim();

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    animation: 'overlayFadeIn 0.12s ease-out',
  };

  const modalStyle: React.CSSProperties = {
    position: 'relative',
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '32px',
    width: '420px',
    maxWidth: '90vw',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    // Same entrance as the notification detail modal (index.css keyframes) so
    // the two modal systems read as one.
    animation: 'modalCardIn 0.18s ease-out',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    color: '#202020',
    margin: '0 0 16px 0',
    display: 'block',
  };

  const messageStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#666',
    lineHeight: 1.6,
    margin: '0 0 28px 0',
    display: 'block',
  };

  const buttonsStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
  };

  const cancelBtnStyle: React.CSSProperties = {
    padding: '12px 24px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    backgroundColor: '#fff',
    color: '#333',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    transition: 'all 0.2s',
  };

  const confirmBtnStyle: React.CSSProperties = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#ef4444',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    transition: 'all 0.2s',
  };

  return (
    <div style={overlayStyle} onClick={() => {
      // A stray overlay click must not wipe a half-typed confirmation token;
      // the explicit Cancel button still always works.
      if (requireText && typedText.trim()) return;
      onCancel();
    }}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyle}>{title}</h2>
        <p style={{ ...messageStyle, margin: details || requireText ? '0 0 16px 0' : messageStyle.margin }}>{message}</p>
        {details && (
          <div style={{ fontSize: '14px', color: '#444', lineHeight: 1.6, margin: '0 0 16px 0', maxHeight: '180px', overflowY: 'auto', padding: '10px 14px', backgroundColor: '#f8f8f8', borderRadius: '8px' }}>
            {details}
          </div>
        )}
        {/* The wrapper's display:block is load-bearing, not decoration: every
            page root carries the Clapy reset (`.clapyResets
            :not(span,a,ul,ol,li,p) { display:flex }`), which turned this into a
            flex row — the label sat beside the field and the 100%-wide input
            spilled past the modal's edge, and the <strong> room name became a
            flex box that broke the label across three lines. Inline styles
            outrank the reset's class selector. */}
        {requireText && (
          <div style={{ display: 'block', margin: '0 0 24px 0' }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
              Type <strong style={{ display: 'inline', color: '#202020' }}>{requireText}</strong> to confirm:
            </label>
            <input
              type="text"
              value={typedText}
              onChange={(e) => setTypedText(e.target.value)}
              autoFocus
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit' }}
            />
          </div>
        )}
        <div style={buttonsStyle}>
          <button style={cancelBtnStyle} onClick={onCancel}>
            {cancelText}
          </button>
          <button
            style={{ ...confirmBtnStyle, ...(confirmBlocked ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
            onClick={onConfirm}
            disabled={confirmBlocked}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
});
